import { useState } from "react";
import { createRoot } from "react-dom/client";
import type { HandlerFn } from "../../actions";
import { apiPost, type IRequestError } from "../../../../api";
import { Modal } from "../../../utils/modal";
import { toast } from "../../../toast";
import { invalidateTableCache } from "../../queryCache";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmail(value: string): string {
  if (!value) return "Введите email";
  if (!EMAIL_RE.test(value)) return "Некорректный формат email";
  return "";
}

function sanitizeEmail(raw: string): string {
  return raw.replace(/\s/g, "").toLowerCase();
}

function getErrorMessage(err: IRequestError): string {
  if (err.message && err.message !== "Validation error") {
    return err.message;
  }

  const params = err.params as Record<string, unknown> | undefined;
  if (!params || typeof params !== "object") {
    return "Не удалось отправить приглашение";
  }

  if (typeof params.error === "string" && params.error.trim()) return params.error;
  if (typeof params.detail === "string" && params.detail.trim()) return params.detail;

  // Search for any string error in params (like field-specific errors)
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (typeof first === "string" && first.trim()) {
        return first;
      }
    }
  }

  return "Не удалось отправить приглашение";
}

function SendInviteModal({
  url,
  tableApiUrl,
  onClose,
}: {
  url: string;
  tableApiUrl?: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const clientError = touched ? validateEmail(email) : "";
  const displayError = error || clientError;
  const canSubmit = !!email && !clientError && !submitting;

  const handleChange = (value: string) => {
    setEmail(sanitizeEmail(value));
    setError("");
  };

  const handleSubmit = async () => {
    setTouched(true);
    const validation = validateEmail(email);
    if (validation) {
      setError(validation);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await apiPost(url, { email });
      // If tableApiUrl is missing for some rendering contexts, invalidate all live tables
      // so Invitations still refetch immediately after successful send.
      invalidateTableCache(tableApiUrl);
      toast.success("Приглашение отправлено");
      onClose();
    } catch (e: unknown) {
      console.error("Invite failed:", e);
      const err = e as IRequestError;
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      toast.error(errorMessage);
      invalidateTableCache(tableApiUrl);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={true}
      onClose={onClose}
      title="Отправить приглашение"
      size="sm"
      footer={
        <>
          <button
            className="rd-btn rd-btn--primary rd-btn--large"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? "Отправка..." : "Отправить"}
          </button>
          <button
            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--large"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
        </>
      }
    >
      <div className={`rd-input rd-input--large${displayError ? " rd-input--error" : ""}`}>
        <input
          type="email"
          className="rd-input__field"
          placeholder="Email"
          value={email}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) handleSubmit();
          }}
          disabled={submitting}
          autoFocus
        />
        {displayError && <span className="rd-input__error-text">{displayError}</span>}
      </div>
    </Modal>
  );
}

export const sendInviteHandler: HandlerFn = (ctx) => {
  const url = ctx.config?.url;
  if (!url) return;
  const tableApiUrl = ctx.tableState?.apiUrl;

  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const cleanup = () => {
    try { root.unmount(); } catch { /* already unmounted */ }
    container.remove();
  };

  root.render(
    <SendInviteModal url={url} tableApiUrl={tableApiUrl} onClose={cleanup} />,
  );
};
